/**
 * Voice Referral Component
 * AI-driven voice interface for creating referrals through natural conversation
 *
 * IMPROVEMENTS (Latest Update):
 * ==================================
 *
 * 1. FIXED EXTRACTION BUGS:
 *    - Fixed "name is Viraj Gulati" being extracted as "name is" instead of "Viraj Gulati"
 *    - Improved fullName pattern to properly capture names after trigger words
 *    - Enhanced firstName/lastName patterns with better context awareness
 *    - Increased minimum name length to avoid false matches (e.g., "is Is")
 *
 * 2. CONVERSATION PERSISTENCE & LEARNING:
 *    - All voice conversations are now saved to database (voice_conversations table)
 *    - Each extracted field is logged for machine learning (voice_extracted_fields table)
 *    - Full transcript and conversation messages preserved for review
 *    - Enables future ML model training from successful extractions
 *
 * 3. DATA RETENTION FOR REVIEW:
 *    - Users can review their spoken responses later
 *    - Conversation history includes timestamps and confidence levels
 *    - Extracted data can be corrected by users to improve future extractions
 *    - Original text preserved alongside extracted values for context
 *
 * 4. LEARNING FROM EXISTING REFERRALS:
 *    - Backend analyzes past 90 days of successful referrals for patterns
 *    - Common diagnoses, name formats, and phone formats are extracted
 *    - Patterns can be used to improve future extraction accuracy
 *    - API endpoint: GET /api/voice-conversations/patterns
 *
 * 5. CONTINUOUS IMPROVEMENT:
 *    - Each field extraction saves to learning database
 *    - Confidence levels tracked (high/medium/low)
 *    - User corrections flagged for retraining
 *    - System learns from every voice referral interaction
 *
 * DATABASE SCHEMA:
 * - voice_conversations: Stores complete conversation sessions
 * - voice_extracted_fields: Individual field extractions for ML training
 * - Linked to referrals table when referral is created
 *
 * API ENDPOINTS:
 * - POST /api/voice-conversations (action: create|update|save_field)
 * - GET /api/voice-conversations/{id}
 * - GET /api/voice-conversations/learning?field={fieldName}
 * - GET /api/voice-conversations/patterns
 */

import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Mic,
  Square,
  Play,
  Pause,
  CheckCircle,
  AlertCircle,
  Volume2,
  VolumeX,
  RotateCcw,
  Send,
  Sparkles,
  User,
  Phone,
  Mail,
  MapPin,
  Building,
  FileText,
  Languages
} from 'lucide-react';

interface ExtractedField {
  field: string;
  value: string;
  confidence: 'high' | 'medium' | 'low';
  timestamp: number;
}

interface ConversationMessage {
  type: 'system' | 'user' | 'assistant';
  content: string;
  timestamp: number;
}

interface VoiceReferralProps {
  onComplete: (referralData: any) => void;
  onCancel: () => void;
}

// Conversation flow stages with more natural, persona-driven prompts
const CONVERSATION_STAGES = [
  {
    id: 'intro',
    prompt: "Hi there! I'm here to help you create a new referral. Just speak naturally - tell me about the patient. You can include their name, ID number, and how to reach them.",
    fields: ['firstName', 'lastName', 'memberMedicaidId', 'phoneNumber', 'email'],
    keywords: ['name', 'medicaid', 'id', 'phone', 'email', 'contact']
  },
  {
    id: 'demographics',
    prompt: "Perfect! Now, can you share their date of birth, where they live, and what language they prefer?",
    fields: ['dateOfBirth', 'streetAddress', 'city', 'state', 'zipCode', 'preferredLanguage'],
    keywords: ['birth', 'birthday', 'born', 'address', 'street', 'city', 'state', 'zip', 'language', 'speak']
  },
  {
    id: 'health_plan',
    prompt: "Got it! What's their insurance? Which health plan are they covered under?",
    fields: ['healthPlan', 'planType'],
    keywords: ['health plan', 'insurance', 'anthem', 'kaiser', 'molina', 'managed care', 'fee-for-service']
  },
  {
    id: 'provider',
    prompt: "Great! Tell me about the referring doctor or provider - their name, NPI if you know it, where they work, and their contact number.",
    fields: ['providerName', 'providerNPI', 'providerOrg', 'providerPhone'],
    keywords: ['doctor', 'provider', 'npi', 'organization', 'clinic', 'hospital', 'physician']
  },
  {
    id: 'clinical',
    prompt: "Thanks! Now for the medical details - what's the diagnosis or condition? How urgent is this? And does the patient need help with housing, food, transportation, or anything else?",
    fields: ['primaryDiagnosis', 'urgencyLevel', 'sdohNeeds'],
    keywords: ['diagnosis', 'condition', 'urgent', 'immediate', 'routine', 'housing', 'food', 'transportation', 'mental health']
  },
  {
    id: 'authorization',
    prompt: "Almost there! Quick question - does this need prior authorization? And have you obtained the patient's consent?",
    fields: ['authorizationRequired', 'consentObtained', 'hipaaCompliant'],
    keywords: ['authorization', 'consent', 'hipaa', 'compliant', 'approved', 'permission']
  },
  {
    id: 'additional',
    prompt: "Last thing - is there anything else I should know? Any notes about when the patient is available, or special instructions?",
    fields: ['additionalNotes', 'availability'],
    keywords: ['notes', 'additional', 'availability', 'schedule', 'prefer']
  }
];

// Much more flexible field extraction patterns - captures data without strict trigger words
const FIELD_PATTERNS = {
  // Name patterns - VERY flexible, catches names in many contexts
  fullName: [
    // Match after trigger words like "name is", "called", etc.
    /(?:name|called|patient|member|for|referring)\s+(?:is\s+|are\s+)?([A-Z][a-z]{2,}\s+[A-Z][a-z]{2,})/i,
    // Match any standalone capitalized name (higher specificity)
    /\b([A-Z][a-z]{3,}\s+[A-Z][a-z]{3,})\b/,  // At least 4 letters each (avoids "is Is")
  ],
  firstName: [
    /(?:first\s*name|given\s*name)\s+(?:is\s+)?([A-Z][a-z]{2,})/i,
    /\bname\s+(?:is\s+)?([A-Z][a-z]{2,})(?:\s+[A-Z])/i  // "name is John Smith" -> John
  ],
  lastName: [
    /(?:last\s*name|surname|family\s*name)\s+(?:is\s+)?([A-Z][a-z]{2,})/i,
  ],

  // ID patterns - catches any alphanumeric sequence 8-15 chars
  memberMedicaidId: [
    /\b([A-Z]{3}\d{9,12})\b/i,  // Common format: MED123456789
    /\b([A-Z0-9]{9,15})\b/,  // Any 9-15 alphanumeric
    /(?:medicaid|member|id|identification)\s*(?:is|number)?\s*:?\s*([A-Z0-9]{8,15})/i
  ],

  // Contact patterns - very flexible
  phoneNumber: [
    /\b(\d{3}[-.\s]?\d{3}[-.\s]?\d{4})\b/,  // Any 10-digit number
    /\((\d{3})\)\s*(\d{3})[-.\s]?(\d{4})/,  // (555) 123-4567
    /(?:phone|call|contact|number|mobile|cell)\s*(?:is|number)?\s*:?\s*([\d\(\)\s\-\.]{10,})/i
  ],

  email: /\b([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\b/i,  // Any email pattern

  // Date patterns - multiple formats
  dateOfBirth: [
    /\b(\d{1,2}\/\d{1,2}\/\d{2,4})\b/,  // 1/15/1985
    /\b(\d{1,2}-\d{1,2}-\d{2,4})\b/,  // 1-15-1985
    /((?:january|february|march|april|may|june|july|august|september|october|november|december)\s+\d{1,2}(?:st|nd|rd|th)?,?\s+\d{4})/i,
    /(\d{1,2}\s+(?:january|february|march|april|may|june|july|august|september|october|november|december),?\s+\d{4})/i,
    /(?:born|birth|dob)\s*(?:is|on|was)?\s*:?\s*(.+?)(?=\s+(?:address|phone|lives|in|$))/i
  ],

  // Address patterns
  zipCode: /\b(\d{5}(?:-\d{4})?)\b/,  // Any 5 or 9 digit zip

  streetAddress: [
    /\b(\d+\s+[A-Za-z]+(?:\s+[A-Za-z]+){1,4})\b/,  // Address starting with number
    /(?:address|street|live|located|residence)\s*(?:is|at)?\s*:?\s*(.+?)(?=\s+(?:city|zip|\d{5}|$))/i
  ],

  city: [
    /(?:city|in)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i,
    /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)\s*,?\s*(?:[A-Z]{2}|Missouri|California|Texas|New York)/i  // City before state
  ],

  state: [
    /\b(Alabama|Alaska|Arizona|Arkansas|California|Colorado|Connecticut|Delaware|Florida|Georgia|Hawaii|Idaho|Illinois|Indiana|Iowa|Kansas|Kentucky|Louisiana|Maine|Maryland|Massachusetts|Michigan|Minnesota|Mississippi|Missouri|Montana|Nebraska|Nevada|New\s+Hampshire|New\s+Jersey|New\s+Mexico|New\s+York|North\s+Carolina|North\s+Dakota|Ohio|Oklahoma|Oregon|Pennsylvania|Rhode\s+Island|South\s+Carolina|South\s+Dakota|Tennessee|Texas|Utah|Vermont|Virginia|Washington|West\s+Virginia|Wisconsin|Wyoming)\b/i,
    /\b([A-Z]{2})\b(?=\s+\d{5})/  // State abbreviation before zip
  ],

  preferredLanguage: /\b(English|Spanish|Mandarin|Cantonese|Vietnamese|Korean|Tagalog|Arabic|French|Portuguese|Russian|German)\b/i,

  // Health plan - flexible
  healthPlan: [
    /\b((?:Anthem|Kaiser|Molina|United|UnitedHealthcare|Health\s+Net|Aetna|Cigna|Humana)(?:\s+[A-Za-z\s&]+)?)\b/i,
    /(?:insurance|health\s+plan|covered)\s+(?:is|by|with)?\s*([A-Za-z\s&]+)/i
  ],

  planType: /(managed\s+care|fee-for-service|ffs|medicare|medicaid)/i,

  // Diagnosis - catches medical terms
  primaryDiagnosis: [
    /\b(diabetes|hypertension|copd|asthma|heart\s+disease|cancer|depression|anxiety|obesity|[\w\s]+itis|[\w\s]+osis)\b/i,
    /(?:diagnosis|condition|has|suffering)\s+(?:is|from|with)?\s*(.+?)(?=\s+(?:urgent|need|and|$))/i
  ],

  urgencyLevel: /(immediate|emergency|urgent|critical|asap|24\s*hour|week|routine|standard)/i,

  // Provider - flexible
  providerName: [
    /(?:doctor|dr\.?|provider|physician)\s+([A-Z][a-z]+\s+[A-Z][a-z]+)/i,
    /\b(Dr\.?\s+[A-Z][a-z]+(?:\s+[A-Z]\.?)?\s+[A-Z][a-z]+)\b/i
  ],

  providerNPI: /\b(\d{10})\b/,  // Any 10-digit number

  providerOrg: [
    /(?:organization|clinic|hospital|practice|center)\s+(?:is|called)?\s*([A-Z][A-Za-z\s&\.]{3,30})/i,
    /\b([A-Z][a-z]+\s+(?:Hospital|Clinic|Medical\s+Center|Health\s+Center))\b/i
  ],

  providerPhone: /(?:provider|doctor|clinic)\s+(?:phone|number)\s*:?\s*([\d\(\)\s\-\.]{10,})/i,

  // Availability and notes
  availability: /(weekdays?|weekends?|evenings?|mornings?|afternoons?|anytime|any\s+time|flexible|monday|tuesday|wednesday|thursday|friday)/i,

  additionalNotes: /(?:note|additional|also|comment)\s*:?\s*(.{10,})/i
};

// Enhanced SDOH needs extraction with subcategories
const SDOH_KEYWORDS = {
  'Housing Assistance': {
    keywords: ['housing', 'homeless', 'shelter', 'rent', 'eviction', 'apartment', 'home', 'residence', 'dwelling'],
    subcategories: {
      'Housing navigation': ['find housing', 'search', 'locate', 'navigation'],
      'Housing deposits / move-in costs': ['deposit', 'move-in', 'moving cost', 'upfront'],
      'Housing tenancy support': ['tenancy', 'lease', 'landlord', 'tenant'],
      'Transitional housing': ['transitional', 'temporary', 'interim']
    }
  },
  'Food Assistance': {
    keywords: ['food', 'hunger', 'hungry', 'meal', 'nutrition', 'groceries', 'snap', 'eating', 'starving', 'feed'],
    subcategories: {}
  },
  'Transportation': {
    keywords: ['transportation', 'transport', 'ride', 'bus', 'car', 'travel', 'taxi', 'uber', 'lyft', 'get around'],
    subcategories: {
      'Non-Emergency Medical Transportation (NEMT)': ['nemt', 'medical transport', 'doctor appointment', 'hospital ride'],
      'Bus passes / transit vouchers': ['bus pass', 'transit', 'voucher', 'public transport']
    }
  },
  'Mental Health Support': {
    keywords: ['mental health', 'counseling', 'therapy', 'therapist', 'depression', 'anxiety', 'behavioral', 'psychiatric', 'psychologist'],
    subcategories: {}
  },
  'Substance Abuse Treatment': {
    keywords: ['substance', 'abuse', 'addiction', 'alcohol', 'drug', 'rehab', 'recovery'],
    subcategories: {}
  },
  'Financial Assistance': {
    keywords: ['financial', 'money', 'bills', 'debt', 'assistance', 'payment', 'afford', 'cash', 'income'],
    subcategories: {}
  },
  'Healthcare Access': {
    keywords: ['healthcare', 'medical care', 'doctor', 'appointment', 'clinic', 'hospital access'],
    subcategories: {}
  },
  'Education Support': {
    keywords: ['education', 'school', 'learning', 'tutoring', 'ged', 'literacy'],
    subcategories: {}
  },
  'Employment Support': {
    keywords: ['employment', 'job', 'work', 'career', 'resume', 'interview'],
    subcategories: {}
  },
  'Legal Services': {
    keywords: ['legal', 'lawyer', 'attorney', 'court', 'law'],
    subcategories: {}
  },
  'Childcare Support': {
    keywords: ['childcare', 'daycare', 'babysitter', 'children', 'kids'],
    subcategories: {}
  },
  'Utility Assistance': {
    keywords: ['utility', 'utilities', 'electric', 'gas', 'water', 'power', 'heat'],
    subcategories: {}
  }
};

// Helper function to normalize dates from spoken format to YYYY-MM-DD
const normalizeDateOfBirth = (dateStr: string): string => {
  const monthMap: Record<string, string> = {
    january: '01', february: '02', march: '03', april: '04',
    may: '05', june: '06', july: '07', august: '08',
    september: '09', october: '10', november: '11', december: '12'
  };

  // Handle "January 15, 1985" or "15 January 1985"
  const monthDayYear = dateStr.match(/(?:january|february|march|april|may|june|july|august|september|october|november|december)\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})/i);
  if (monthDayYear) {
    const month = dateStr.toLowerCase().match(/(january|february|march|april|may|june|july|august|september|october|november|december)/)?.[1];
    if (month) {
      const day = monthDayYear[1].padStart(2, '0');
      return `${monthDayYear[2]}-${monthMap[month]}-${day}`;
    }
  }

  const dayMonthYear = dateStr.match(/(\d{1,2})\s+(january|february|march|april|may|june|july|august|september|october|november|december),?\s+(\d{4})/i);
  if (dayMonthYear) {
    const day = dayMonthYear[1].padStart(2, '0');
    const month = monthMap[dayMonthYear[2].toLowerCase()];
    return `${dayMonthYear[3]}-${month}-${day}`;
  }

  // Handle MM/DD/YYYY or MM-DD-YYYY
  const slashDate = dateStr.match(/(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})/);
  if (slashDate) {
    const month = slashDate[1].padStart(2, '0');
    const day = slashDate[2].padStart(2, '0');
    let year = slashDate[3];
    if (year.length === 2) {
      year = (parseInt(year) > 30 ? '19' : '20') + year;
    }
    return `${year}-${month}-${day}`;
  }

  return dateStr; // Return as-is if no pattern matches
};

// Helper function to normalize urgency levels
const normalizeUrgencyLevel = (urgency: string): string => {
  const lower = urgency.toLowerCase();
  if (lower.includes('immediate') || lower.includes('emergency') || lower.includes('critical') || lower.includes('asap')) {
    return 'Immediate';
  }
  if (lower.includes('24 hour') || lower.includes('24hour') || lower.includes('tomorrow')) {
    return 'Within 24h';
  }
  if (lower.includes('week') || lower.includes('7 day')) {
    return 'Within 1 Week';
  }
  return 'Routine';
};

// Helper function to format phone numbers
const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned[0] === '1') {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

export function VoiceReferral({ onComplete, onCancel }: VoiceReferralProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentStage, setCurrentStage] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [extractedFields, setExtractedFields] = useState<ExtractedField[]>([]);
  const [conversation, setConversation] = useState<ConversationMessage[]>([]);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false); // Start with voice OFF
  const [language, setLanguage] = useState('en-US');
  const [error, setError] = useState<string>('');
  const [hasInitialized, setHasInitialized] = useState(false);
  const [noSpeechCount, setNoSpeechCount] = useState(0);
  const [lastSpeechTime, setLastSpeechTime] = useState<number>(0);

  // Conversation persistence state
  const [sessionId] = useState(() => `voice-session-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`);
  const [conversationId, setConversationId] = useState<number | null>(null);

  const recognitionRef = useRef<any>(null);
  const synthRef = useRef<SpeechSynthesisUtterance | null>(null);
  const isSpeakingRef = useRef(false);
  const isRecordingRef = useRef(false);
  const isPausedRef = useRef(false);

  // Use relative URL or current origin to avoid CORS issues with www subdomain
  const apiBaseUrl = window.location.origin;

  // Initialize Speech Recognition
  useEffect(() => {
    console.log('🔧 Initializing speech recognition...');
    console.log('  webkitSpeechRecognition available:', 'webkitSpeechRecognition' in window);
    console.log('  SpeechRecognition available:', 'SpeechRecognition' in window);

    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = language;

      console.log('✅ Speech recognition initialized with language:', language);

      recognitionRef.current.onresult = (event: any) => {
        let interim = '';
        let final = '';

        console.log('🎤 Speech recognition result event:', event.results.length, 'results');

        // Reset no-speech counter when we get results
        setNoSpeechCount(0);
        setLastSpeechTime(Date.now());

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcriptText = event.results[i][0].transcript;
          console.log(`  Result ${i}: "${transcriptText}" (final: ${event.results[i].isFinal})`);
          if (event.results[i].isFinal) {
            final += transcriptText + ' ';
          } else {
            interim += transcriptText;
          }
        }

        if (final) {
          console.log('✅ Final transcript:', final);
          setTranscript(prev => {
            const newTranscript = prev + final;
            console.log('📝 Updated transcript:', newTranscript);
            return newTranscript;
          });
          extractFieldsFromText(final);
          addConversationMessage('user', final.trim());
        } else if (interim) {
          console.log('⏳ Interim transcript:', interim);
        }
      };

      recognitionRef.current.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);

        if (event.error === 'no-speech') {
          setNoSpeechCount(prev => prev + 1);
          // After 3 no-speech errors, show helpful message
          if (noSpeechCount >= 2) {
            console.warn('⚠️ Multiple no-speech errors. Check microphone.');
          }
        } else {
          setError(`Recognition error: ${event.error}`);
        }
      };

      recognitionRef.current.onend = () => {
        console.log('🔚 Recognition ended, checking if should restart...', {
          isRecording: isRecordingRef.current,
          isPaused: isPausedRef.current
        });
        if (isRecordingRef.current && !isPausedRef.current) {
          console.log('♻️  Restarting recognition...');
          try {
            recognitionRef.current?.start();
          } catch (e) {
            console.error('Failed to restart recognition:', e);
          }
        }
      };
    } else {
      setError('Speech recognition is not supported in this browser. Please use Chrome or Edge.');
    }

    // Only speak intro once on mount
    if (!hasInitialized) {
      setHasInitialized(true);
      // Add conversation message immediately
      addConversationMessage('assistant', CONVERSATION_STAGES[0].prompt);

      // Delay initial speech to ensure voices are loaded
      setTimeout(() => {
        speakText(CONVERSATION_STAGES[0].prompt);
      }, 500);
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, [hasInitialized]);

  const speakText = (text: string) => {
    if (!voiceEnabled || !window.speechSynthesis) return;

    // Prevent overlapping speech
    if (isSpeakingRef.current) {
      console.log('Already speaking, skipping...');
      return;
    }

    // Cancel any existing speech
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
    }

    // Wait for cancel to complete
    setTimeout(() => {
      const utterance = new SpeechSynthesisUtterance(text);

      // Map language codes to proper voice language codes
      const languageMap: Record<string, string> = {
        'en-US': 'en-US',
        'es-ES': 'es-ES',
        'es-MX': 'es-MX',
        'fr-FR': 'fr-FR',
        'de-DE': 'de-DE',
        'it-IT': 'it-IT',
        'pt-BR': 'pt-BR',
        'zh-CN': 'zh-CN',
        'ar-SA': 'ar-SA'
      };

      utterance.lang = languageMap[language] || language;
      utterance.rate = 0.85;  // Slightly slower for better clarity
      utterance.pitch = 1.1;  // Slightly higher for friendlier tone
      utterance.volume = 1.0;

      // Try to select a natural voice for the language
      const voices = window.speechSynthesis.getVoices();
      const preferredVoice = voices.find(voice =>
        voice.lang.startsWith(utterance.lang) &&
        (voice.name.includes('Natural') || voice.name.includes('Enhanced') || voice.localService)
      );
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }

      utterance.onstart = () => {
        isSpeakingRef.current = true;
        setIsSpeaking(true);
      };

      utterance.onend = () => {
        isSpeakingRef.current = false;
        setIsSpeaking(false);
      };

      utterance.onerror = (error) => {
        // Only log non-interrupted errors
        if (error.error !== 'interrupted') {
          console.error('Speech synthesis error:', error);
        }
        isSpeakingRef.current = false;
        setIsSpeaking(false);
      };

      synthRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    }, 150);
  };

  const addConversationMessage = (type: 'system' | 'user' | 'assistant', content: string) => {
    setConversation(prev => [...prev, {
      type,
      content,
      timestamp: Date.now()
    }]);
  };

  const extractFieldsFromText = (text: string) => {
    const newFields: ExtractedField[] = [];
    const lowerText = text.toLowerCase();

    console.log('🔍 Extracting from text:', text);

    // Extract using patterns (handle both single patterns and arrays)
    Object.entries(FIELD_PATTERNS).forEach(([field, patterns]) => {
      // Convert to array if single pattern
      const patternArray = Array.isArray(patterns) ? patterns : [patterns];

      // Try each pattern until we find a match
      for (const pattern of patternArray) {
        const match = text.match(pattern);
        if (match) {
          // Get the first non-empty captured group
          let value = '';
          for (let i = 1; i < match.length; i++) {
            if (match[i]) {
              value = match[i].trim();
              break;
            }
          }

          if (!value) continue;

          console.log(`✅ Found ${field}:`, value);

          // Apply normalizations
          if (field === 'dateOfBirth' && value) {
            value = normalizeDateOfBirth(value);
          }
          if (field === 'urgencyLevel' && value) {
            value = normalizeUrgencyLevel(value);
          }
          if ((field === 'phoneNumber' || field === 'providerPhone') && value) {
            value = formatPhoneNumber(value);
          }

          // Handle full name extraction - split into first and last
          if (field === 'fullName' && value) {
            const nameParts = value.split(/\s+/);
            if (nameParts.length >= 2) {
              newFields.push({
                field: 'firstName',
                value: nameParts[0],
                confidence: 'high',
                timestamp: Date.now()
              });
              newFields.push({
                field: 'lastName',
                value: nameParts.slice(1).join(' '),
                confidence: 'high',
                timestamp: Date.now()
              });
              console.log(`✅ Split name into firstName: ${nameParts[0]}, lastName: ${nameParts.slice(1).join(' ')}`);
            }
          } else if (value) {
            newFields.push({
              field,
              value,
              confidence: 'high',
              timestamp: Date.now()
            });
          }

          // Break after first successful match for this field
          break;
        }
      }
    });

    // Extract SDOH needs with subcategories
    const sdohNeeds: string[] = [];
    const housingSubcategories: string[] = [];
    const transportationSubcategories: string[] = [];

    Object.entries(SDOH_KEYWORDS).forEach(([need, config]) => {
      // Check main keywords
      if (config.keywords.some((keyword: string) => lowerText.includes(keyword.toLowerCase()))) {
        sdohNeeds.push(need);

        // Check for subcategories
        if (need === 'Housing Assistance') {
          Object.entries(config.subcategories).forEach(([subcat, subKeywords]) => {
            if ((subKeywords as string[]).some((kw: string) => lowerText.includes(kw.toLowerCase()))) {
              housingSubcategories.push(subcat);
            }
          });
        }

        if (need === 'Transportation') {
          Object.entries(config.subcategories).forEach(([subcat, subKeywords]) => {
            if ((subKeywords as string[]).some((kw: string) => lowerText.includes(kw.toLowerCase()))) {
              transportationSubcategories.push(subcat);
            }
          });
        }
      }
    });

    if (sdohNeeds.length > 0) {
      newFields.push({
        field: 'sdohNeeds',
        value: sdohNeeds.join(','),
        confidence: 'medium',
        timestamp: Date.now()
      });
    }

    if (housingSubcategories.length > 0) {
      newFields.push({
        field: 'housingSubcategories',
        value: housingSubcategories.join(','),
        confidence: 'medium',
        timestamp: Date.now()
      });
    }

    if (transportationSubcategories.length > 0) {
      newFields.push({
        field: 'transportationSubcategories',
        value: transportationSubcategories.join(','),
        confidence: 'medium',
        timestamp: Date.now()
      });
    }

    // Context-aware extraction based on current stage
    const stage = CONVERSATION_STAGES[currentStage];
    newFields.forEach(field => {
      // Boost confidence if field matches current stage
      if (stage.fields.includes(field.field)) {
        field.confidence = 'high';
      }
    });

    // Log extraction results
    if (newFields.length > 0) {
      console.log(`✨ Extracted ${newFields.length} fields:`, newFields);
    } else {
      console.log('⚠️ No fields extracted from text');
    }

    // Update extracted fields (avoid duplicates)
    setExtractedFields(prev => {
      const updated = [...prev];
      newFields.forEach(newField => {
        const existingIndex = updated.findIndex(f => f.field === newField.field);
        if (existingIndex >= 0) {
          // Update if new confidence is higher or same
          if (newField.confidence === 'high' || updated[existingIndex].confidence !== 'high') {
            console.log(`🔄 Updating field ${newField.field}: ${newField.value}`);
            updated[existingIndex] = newField;
            // Save field for learning
            saveExtractedField(newField.field, newField.value, text, newField.confidence);
          }
        } else {
          console.log(`➕ Adding new field ${newField.field}: ${newField.value}`);
          updated.push(newField);
          // Save field for learning
          saveExtractedField(newField.field, newField.value, text, newField.confidence);
        }
      });
      console.log(`📊 Total extracted fields now: ${updated.length}`, updated);
      return updated;
    });

    // Periodically update conversation with current state
    if (newFields.length > 0) {
      updateConversation();
    }
  };

  // Conversation persistence functions
  const createConversation = async () => {
    try {
      const url = `${apiBaseUrl}/api/voice-conversations`;
      console.log('📡 Creating conversation at:', url);
      console.log('  Session ID:', sessionId);
      console.log('  Language:', language);

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create',
          session_id: sessionId,
          language: language,
        }),
      });

      console.log('📡 Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        setConversationId(data.conversation_id);
        console.log('✅ Created conversation:', data.conversation_id);
        return data.conversation_id;
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to create conversation:', response.status, errorText);
      }
    } catch (error) {
      console.error('❌ Network error creating conversation:', error);
    }
    return null;
  };

  const updateConversation = async () => {
    if (!conversationId) return;

    try {
      const extractedData: any = {};
      extractedFields.forEach(field => {
        extractedData[field.field] = field.value;
      });

      await fetch(`${apiBaseUrl}/api/voice-conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'update',
          conversation_id: conversationId,
          transcript: transcript,
          extracted_data: extractedData,
          conversation_messages: conversation,
        }),
      });

      console.log('✅ Updated conversation');
    } catch (error) {
      console.error('Failed to update conversation:', error);
    }
  };

  const saveExtractedField = async (fieldName: string, value: string, originalText: string, confidence: string) => {
    if (!conversationId) return;

    try {
      await fetch(`${apiBaseUrl}/api/voice-conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'save_field',
          conversation_id: conversationId,
          field_name: fieldName,
          extracted_value: value,
          original_text: originalText,
          confidence: confidence,
        }),
      });

      console.log(`✅ Saved field ${fieldName} for learning`);
    } catch (error) {
      console.error('Failed to save field:', error);
    }
  };

  const startRecording = async () => {
    console.log('🎙️ startRecording called, recognitionRef exists:', !!recognitionRef.current);
    console.log('🌐 Current URL:', window.location.href);
    console.log('🔒 Is HTTPS:', window.location.protocol === 'https:');

    if (!recognitionRef.current) {
      const errorMsg = 'Speech recognition not initialized. Please refresh the page or try a different browser (Chrome/Edge recommended).';
      setError(errorMsg);
      console.error('❌', errorMsg);
      return;
    }

    try {
      // Request microphone permission explicitly
      try {
        console.log('🎤 Requesting microphone permission...');
        await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log('✅ Microphone permission granted');
      } catch (permError: any) {
        console.error('❌ Microphone permission denied:', permError);
        setError('Microphone access denied. Please enable microphone permissions in your browser settings.');
        return;
      }

      // Create conversation on first recording start
      if (!conversationId) {
        console.log('📝 Creating conversation...');
        await createConversation();
      }

      console.log('▶️ Starting speech recognition...');
      console.log('  Language:', recognitionRef.current.lang);
      console.log('  Continuous:', recognitionRef.current.continuous);
      console.log('  InterimResults:', recognitionRef.current.interimResults);

      recognitionRef.current.start();
      setIsRecording(true);
      isRecordingRef.current = true;
      isPausedRef.current = false;
      setNoSpeechCount(0);
      setLastSpeechTime(Date.now());
      setError('');
      console.log('✅ Recording started successfully - speak now!');
    } catch (e: any) {
      console.error('❌ Failed to start recognition:', e);
      console.error('  Error name:', e.name);
      console.error('  Error message:', e.message);

      // If already started, that's okay
      if (e.message && e.message.includes('already started')) {
        console.log('ℹ️ Recognition already started, continuing...');
        setIsRecording(true);
        isRecordingRef.current = true;
        isPausedRef.current = false;
        setError('');
      } else {
        const errorMsg = `Failed to start recording: ${e.message || 'Unknown error'}. Make sure you're using HTTPS and have granted microphone permissions.`;
        setError(errorMsg);
        console.error('❌', errorMsg);
      }
    }
  };

  const stopRecording = () => {
    console.log('⏹️ Stopping recording...');
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      isRecordingRef.current = false;
      isPausedRef.current = false;
    }
  };

  const pauseRecording = () => {
    console.log('⏸️ Toggling pause...');
    if (recognitionRef.current && isRecording) {
      if (isPaused) {
        console.log('▶️ Resuming...');
        recognitionRef.current.start();
        setIsPaused(false);
        isPausedRef.current = false;
      } else {
        console.log('⏸️ Pausing...');
        recognitionRef.current.stop();
        setIsPaused(true);
        isPausedRef.current = true;
      }
    }
  };

  const nextStage = () => {
    if (currentStage < CONVERSATION_STAGES.length - 1) {
      const nextStageNum = currentStage + 1;
      setCurrentStage(nextStageNum);
      const stage = CONVERSATION_STAGES[nextStageNum];
      speakText(stage.prompt);
      addConversationMessage('assistant', stage.prompt);
    } else {
      // All stages complete
      completeReferral();
    }
  };

  const previousStage = () => {
    if (currentStage > 0) {
      const prevStageNum = currentStage - 1;
      setCurrentStage(prevStageNum);
      const stage = CONVERSATION_STAGES[prevStageNum];
      speakText("Let's go back. " + stage.prompt);
      addConversationMessage('assistant', "Let's go back. " + stage.prompt);
    }
  };

  const resetConversation = () => {
    console.log('🔄 Resetting conversation...');

    // Stop any ongoing recognition and speech
    if (recognitionRef.current && isRecording) {
      console.log('⏹️ Stopping recognition before reset...');
      recognitionRef.current.stop();
      setIsRecording(false);
      isRecordingRef.current = false;
      isPausedRef.current = false;
    }

    if (window.speechSynthesis.speaking) {
      console.log('🔇 Canceling speech before reset...');
      window.speechSynthesis.cancel();
      isSpeakingRef.current = false;
    }

    // Reset state
    setCurrentStage(0);
    setTranscript('');
    setExtractedFields([]);
    setIsPaused(false);

    // Add message and speak after a brief delay to ensure everything is stopped
    setTimeout(() => {
      setConversation([]);
      addConversationMessage('assistant', CONVERSATION_STAGES[0].prompt);
      speakText(CONVERSATION_STAGES[0].prompt);
      console.log('✅ Conversation reset complete');
    }, 100);
  };

  const completeReferral = () => {
    // Convert extracted fields to referral data with proper structure
    const referralData: any = {
      useMatchingEngine: true,
      status: 'Pending',
      timeline: 'Routine',
      authorizationRequired: false,
      consentObtained: true,
      hipaaCompliant: true,
      sdohNeeds: [],
      housingSubcategories: [],
      transportationSubcategories: [],
      country: 'USA',
      planType: 'Managed Care'
    };

    console.log('📊 Extracted fields before mapping:', extractedFields);

    extractedFields.forEach(field => {
      const fieldName = field.field;
      const fieldValue = field.value;

      // Handle SDOH needs
      if (fieldName === 'sdohNeeds') {
        referralData.sdohNeeds = fieldValue.split(',').filter((n: string) => n.trim());
      }
      // Handle housing subcategories
      else if (fieldName === 'housingSubcategories') {
        referralData.housingSubcategories = fieldValue.split(',').filter((n: string) => n.trim());
      }
      // Handle transportation subcategories
      else if (fieldName === 'transportationSubcategories') {
        referralData.transportationSubcategories = fieldValue.split(',').filter((n: string) => n.trim());
      }
      // Handle urgency level
      else if (fieldName === 'urgencyLevel') {
        referralData.urgencyLevel = fieldValue;
        referralData.timeline = fieldValue;
      }
      // Handle provider fields
      else if (fieldName.startsWith('provider')) {
        if (!referralData.referringProvider) {
          referralData.referringProvider = {};
        }
        if (fieldName === 'providerName') {
          referralData.referringProvider.name = fieldValue;
        } else if (fieldName === 'providerNPI') {
          referralData.referringProvider.npi = fieldValue;
        } else if (fieldName === 'providerOrg') {
          referralData.referringProvider.organization = fieldValue;
        } else if (fieldName === 'providerPhone') {
          referralData.referringProvider.phone = fieldValue;
        }
      }
      // Handle plan type normalization
      else if (fieldName === 'planType') {
        if (fieldValue.toLowerCase().includes('fee')) {
          referralData.planType = 'Fee-for-Service';
        } else {
          referralData.planType = 'Managed Care';
        }
      }
      // Map all other fields directly
      else {
        referralData[fieldName] = fieldValue;
      }
    });

    // Ensure provider object exists
    if (!referralData.referringProvider) {
      referralData.referringProvider = {
        name: '',
        npi: '',
        organization: '',
        phone: ''
      };
    }

    // SECURITY: Removed PII logging - referralData contains firstName, lastName, phone, email, etc.
    console.log('📤 Final referral data prepared');

    // Save final conversation state before completion
    updateConversation();

    speakText("Thank you! I've collected all the information. You can now review and submit the referral.");
    onComplete(referralData);
  };

  const getFieldDisplayName = (field: string): string => {
    const names: Record<string, string> = {
      firstName: 'First Name',
      lastName: 'Last Name',
      memberMedicaidId: 'Medicaid ID',
      phoneNumber: 'Phone Number',
      email: 'Email',
      dateOfBirth: 'Date of Birth',
      streetAddress: 'Street Address',
      city: 'City',
      state: 'State',
      zipCode: 'ZIP Code',
      preferredLanguage: 'Preferred Language',
      healthPlan: 'Health Plan',
      planType: 'Plan Type',
      providerName: 'Provider Name',
      providerNPI: 'Provider NPI',
      providerOrg: 'Provider Organization',
      providerPhone: 'Provider Phone',
      primaryDiagnosis: 'Primary Diagnosis',
      urgencyLevel: 'Urgency Level',
      sdohNeeds: 'Social Needs',
      additionalNotes: 'Additional Notes',
      availability: 'Availability'
    };
    return names[field] || field;
  };

  const toggleVoice = () => {
    setVoiceEnabled(!voiceEnabled);
    if (voiceEnabled) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  };

  const stage = CONVERSATION_STAGES[currentStage];
  const progress = ((currentStage + 1) / CONVERSATION_STAGES.length) * 100;

  return (
    <div className="space-y-6">
      {/* Progress Bar */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-gray-700">
            Stage {currentStage + 1} of {CONVERSATION_STAGES.length}: {stage.id}
          </span>
          <span className="text-sm font-semibold text-blue-600">
            {Math.round(progress)}% Complete
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="bg-gradient-to-r from-blue-500 to-indigo-600 h-3 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Language Selection */}
      <div className="flex items-center gap-4 bg-white rounded-xl shadow-md p-4">
        <Languages className="w-5 h-5 text-blue-600" />
        <Select value={language} onValueChange={setLanguage} disabled={isRecording}>
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="en-US">English (US)</SelectItem>
            <SelectItem value="es-ES">Spanish</SelectItem>
            <SelectItem value="fr-FR">French</SelectItem>
            <SelectItem value="zh-CN">Chinese</SelectItem>
            <SelectItem value="vi-VN">Vietnamese</SelectItem>
            <SelectItem value="ko-KR">Korean</SelectItem>
            <SelectItem value="ar-SA">Arabic</SelectItem>
            <SelectItem value="hi-IN">Hindi</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex-1" />

        <Button
          variant="outline"
          size="sm"
          onClick={toggleVoice}
          className={voiceEnabled ? 'border-green-400' : 'border-gray-300'}
        >
          {voiceEnabled ? (
            <>
              <Volume2 className="w-4 h-4 mr-2" />
              Voice On
            </>
          ) : (
            <>
              <VolumeX className="w-4 h-4 mr-2" />
              Voice Off
            </>
          )}
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Microphone Warning */}
      {isRecording && noSpeechCount >= 3 && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-900">
              <p className="font-semibold mb-1">⚠️ Microphone Not Detecting Speech</p>
              <p className="mb-2">The microphone is active but not picking up your voice. Please check:</p>
              <ul className="list-disc ml-5 space-y-1">
                <li>Your microphone is not muted (check system settings)</li>
                <li>The correct microphone is selected in your browser</li>
                <li>You're speaking clearly and loudly enough</li>
                <li>No other app is using the microphone</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recording Controls */}
        <Card className="border-2 border-blue-200 shadow-xl">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardTitle className="flex items-center gap-2">
              <Mic className="w-5 h-5 text-blue-600" />
              Voice Recording
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            {/* Recording Controls */}
            <div className="flex items-center justify-center gap-3">
              {!isRecording ? (
                <Button
                  onClick={startRecording}
                  size="lg"
                  className="bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700"
                >
                  <Mic className="w-5 h-5 mr-2" />
                  Start Speaking
                </Button>
              ) : (
                <>
                  <Button
                    onClick={stopRecording}
                    size="lg"
                    className="bg-gradient-to-r from-gray-600 to-gray-700"
                  >
                    <Square className="w-5 h-5 mr-2" />
                    Stop
                  </Button>
                  <Button
                    onClick={pauseRecording}
                    size="lg"
                    variant="outline"
                    className="border-2 border-blue-400"
                  >
                    {isPaused ? (
                      <>
                        <Play className="w-5 h-5 mr-2" />
                        Resume
                      </>
                    ) : (
                      <>
                        <Pause className="w-5 h-5 mr-2" />
                        Pause
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>

            {/* AI Guidance */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border-2 border-purple-200">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5 animate-pulse" />
                <div>
                  <h4 className="font-semibold text-purple-900 mb-1">
                    AI Guidance {isSpeaking && <span className="text-xs">(Speaking...)</span>}
                  </h4>
                  <p className="text-sm text-purple-800">{stage.prompt}</p>
                </div>
              </div>
            </div>

            {/* Live Transcript */}
            {isRecording && (
              <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Live Transcript
                  {isRecording && !isPaused && (
                    <span className="animate-pulse text-red-500">⏺</span>
                  )}
                </h4>
                <p className="text-sm text-gray-800 min-h-[80px] max-h-[200px] overflow-y-auto">
                  {transcript || <em className="text-gray-500">Listening...</em>}
                </p>
              </div>
            )}

            {/* Navigation */}
            <div className="flex gap-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={previousStage}
                disabled={currentStage === 0}
                className="flex-1"
              >
                Previous Stage
              </Button>
              <Button
                variant="outline"
                onClick={resetConversation}
                className="flex-1"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Restart
              </Button>
              <Button
                onClick={nextStage}
                className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600"
              >
                {currentStage === CONVERSATION_STAGES.length - 1 ? 'Complete' : 'Next Stage'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Extracted Information */}
        <Card className="border-2 border-green-200 shadow-xl">
          <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Extracted Information ({extractedFields.length} fields)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {extractedFields.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <AlertCircle className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p>No information extracted yet.</p>
                <p className="text-sm mt-1">Start speaking to capture data.</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {extractedFields.map((field, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border-2 ${
                      field.confidence === 'high'
                        ? 'bg-green-50 border-green-200'
                        : field.confidence === 'medium'
                        ? 'bg-yellow-50 border-yellow-200'
                        : 'bg-orange-50 border-orange-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {getFieldIcon(field.field)}
                          <span className="font-semibold text-gray-900">
                            {getFieldDisplayName(field.field)}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {field.confidence}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-700 mt-1">{field.value}</p>
                      </div>
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {extractedFields.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <Button
                  onClick={completeReferral}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600"
                  size="lg"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Submit Referral Now
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Conversation History */}
      <Card className="border-2 border-gray-200 shadow-xl">
        <CardHeader className="bg-gradient-to-r from-gray-50 to-slate-50">
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-gray-600" />
            Conversation History
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-3 max-h-[300px] overflow-y-auto">
            {conversation.length === 0 ? (
              <p className="text-center text-gray-500">Conversation will appear here...</p>
            ) : (
              conversation.map((msg, index) => (
                <div
                  key={index}
                  className={`p-3 rounded-lg ${
                    msg.type === 'assistant'
                      ? 'bg-purple-50 border-l-4 border-purple-400'
                      : msg.type === 'user'
                      ? 'bg-blue-50 border-l-4 border-blue-400 ml-8'
                      : 'bg-gray-50 border-l-4 border-gray-400'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {msg.type === 'assistant' && <Sparkles className="w-4 h-4 text-purple-600" />}
                    {msg.type === 'user' && <User className="w-4 h-4 text-blue-600" />}
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-700 mb-1">
                        {msg.type === 'assistant' ? 'AI Assistant' : 'You'}
                      </p>
                      <p className="text-sm text-gray-800">{msg.content}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Helper function to get icon for field
function getFieldIcon(field: string) {
  const iconClass = "w-4 h-4 text-blue-600";
  if (field.includes('name') || field.includes('Name')) return <User className={iconClass} />;
  if (field.includes('phone') || field.includes('Phone')) return <Phone className={iconClass} />;
  if (field.includes('email')) return <Mail className={iconClass} />;
  if (field.includes('address') || field.includes('city') || field.includes('state') || field.includes('zip')) return <MapPin className={iconClass} />;
  if (field.includes('provider') || field.includes('Provider') || field.includes('health') || field.includes('plan')) return <Building className={iconClass} />;
  return <FileText className={iconClass} />;
}
